import { z } from 'zod'

import { dateSchema, numberOrStringSchema } from '@shared/schema/utils'

export const AirQualityMetricSchema = z.union([z.literal('co2'), z.literal('humidity'), z.literal('tvoc')])

export const AirQualityBandSchema = z.union([z.literal('good'), z.literal('fair'), z.literal('poor')])

export const GetSiteAirQualityComfortPercentageQueryStringSchema = z.object({
  startDate: dateSchema,
  endDate: dateSchema,
  roomType: z.union([z.literal('office'), z.literal('room')]).optional(),
  siteIds: z
    .string()
    .regex(/^(?!.*,,)(?!.*,$)(?!^,).*$/)
    .transform((value) => value.split(','))
    .optional(),
  buildingIds: z
    .string()
    .regex(/^(?!.*,,)(?!.*,$)(?!^,).*$/)
    .transform((value) => value.split(','))
    .optional(),
  floorIds: z
    .string()
    .regex(/^(?!.*,,)(?!.*,$)(?!^,).*$/)
    .transform((value) => value.split(','))
    .optional(),
})

const statsSchema = z
  .object({
    avg: z.number().nullable().optional(),
    min: z.number().nullable().optional(),
    max: z.number().nullable().optional(),
  })
  .nullable()
  .optional()

const roomSchema = z.object({
  site: z.string(),
  siteId: numberOrStringSchema,
  building: z.string().nullable().optional(),
  buildingId: numberOrStringSchema.nullable().optional(),
  floor: z.string().nullable().optional(),
  floorId: numberOrStringSchema.nullable().optional(),
  space: z.string().nullable().optional(),
  spaceId: numberOrStringSchema.nullable().optional(),
  deviceId: z.string().nullable().optional(),
  // Temperature probe of the same room, populated for humidity only: humidity
  // comfort is read against the temperature it occurs at.
  temperatureDeviceId: z.string().nullable().optional(),
  equipmentName: z.string().nullable().optional(),
  band: AirQualityBandSchema,
  percentageOut: z.number(),
  avg: z.number().nullable().optional(),
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
})

const metricResultSchema = z.object({
  good: z.number(),
  fair: z.number(),
  poor: z.number(),
  stats: statsSchema,
  roomList: z.array(roomSchema),
})

export const GetSiteAirQualityComfortPercentageResponseSchema = z.object({
  co2: metricResultSchema,
  humidity: metricResultSchema,
  tvoc: metricResultSchema,
})

export type AirQualityMetric = z.infer<typeof AirQualityMetricSchema>
export type AirQualityBand = z.infer<typeof AirQualityBandSchema>
export type AirQualityRoom = z.infer<typeof roomSchema>
export type AirQualityMetricResult = z.infer<typeof metricResultSchema>
export type GetSiteAirQualityComfortPercentageQueryString = z.infer<typeof GetSiteAirQualityComfortPercentageQueryStringSchema>
export type GetSiteAirQualityComfortPercentageResponse = z.infer<typeof GetSiteAirQualityComfortPercentageResponseSchema>
